import { useEffect, useState } from 'react';

// Field messages for a contact dialog. `validate` returns { field: i18n key } for the form's
// current values. A field is checked as it is left, every field on submit, and from then on a
// field that has been checked follows each change as it is typed.
const useFieldErrors = validate => {
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [changes, setChanges] = useState(0);

  // Runs after the render that carries the change, so a controlled form's state is current.
  // Every checked field is rechecked, as one field can settle another (email and its
  // confirmation, the card number and its expiry/CVV).
  useEffect(() => {
    if (!changes) return;
    const errors = validate();
    setFieldErrors(prev => {
      if (submitted) return errors;
      const next = { ...prev };
      Object.keys(touched).forEach(field => {
        next[field] = errors[field];
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changes]);

  // For the form's onChange: every input's change bubbles to it.
  const fieldChanged = () => setChanges(count => count + 1);

  const validateField = field => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setFieldErrors(prev => ({ ...prev, [field]: validate()[field] }));
  };

  // Returns whether the form can be sent.
  const validateAll = () => {
    const errors = validate();
    setSubmitted(true);
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const clearField = field => setFieldErrors(prev => ({ ...prev, [field]: undefined }));

  const clearAll = () => {
    setFieldErrors({});
    setTouched({});
    setSubmitted(false);
  };

  const invalid = field => (fieldErrors[field] ? 'is-invalid' : '');

  return { fieldErrors, fieldChanged, validateField, validateAll, clearField, clearAll, invalid };
};

export default useFieldErrors;
